import { action, makeObservable, observable, runInAction } from "mobx";
import SwagstoreWalletAPI from "../../../api/SwagstoreWalletAPI";
import { PaginatedAPIResponse } from "../../../models/NetworkResponse";
import {
  DEFAULT_WALLET_LIST_QUERY,
  WalletApiRow,
  WalletListQuery,
  WalletType,
} from "../../../models/Swagstore/SwagstoreWalletListing";
import {
  buildDefaultWalletTransactionQuery,
  WalletTransactionApiRow,
  WalletTransactionDownloadResult,
  WalletTransactionListQuery,
} from "../../../models/Swagstore/SwagstoreWalletTransactions";

/**
 * Scoped to the wallet listing page (instantiated per-mount via useRef)
 * rather than registered in the global root store, since this state has
 * no other consumers.
 */
export class SwagstoreWalletStore {
  private readonly api = new SwagstoreWalletAPI();
  private requestSeq = 0;
  private transactionsRequestSeq = 0;

  wallets: WalletApiRow[] = [];
  query: WalletListQuery;
  totalCount = 0;
  totalPages = 0;
  loading = false;
  error: string | null = null;

  transactionsDrawerWalletId: number | null = null;
  transactionsDrawerCustomerName: string | null = null;
  transactionsDrawerWalletType: WalletType = WalletType.CUSTOMER;
  transactions: WalletTransactionApiRow[] = [];
  transactionsQuery: WalletTransactionListQuery | null = null;
  transactionsTotalCount = 0;
  transactionsTotalPages = 0;
  transactionsLoading = false;
  transactionsDownloadLoading = false;
  transactionsError: string | null = null;

  constructor(initialQuery: WalletListQuery = DEFAULT_WALLET_LIST_QUERY) {
    this.query = initialQuery;

    makeObservable(this, {
      wallets: observable,
      query: observable.ref,
      totalCount: observable,
      totalPages: observable,
      loading: observable,
      error: observable,
      setQuery: action,
      fetchWallets: action,
      transactionsDrawerWalletId: observable,
      transactionsDrawerCustomerName: observable,
      transactionsDrawerWalletType: observable,
      transactions: observable,
      transactionsQuery: observable.ref,
      transactionsTotalCount: observable,
      transactionsTotalPages: observable,
      transactionsLoading: observable,
      transactionsDownloadLoading: observable,
      transactionsError: observable,
      openTransactionsDrawer: action,
      closeTransactionsDrawer: action,
      setTransactionsQuery: action,
      fetchTransactions: action,
      downloadTransactionsReport: action,
      clearTransactionsError: action,
    });
  }

  setQuery = (patch: Partial<WalletListQuery>) => {
    this.query = { ...this.query, ...patch };
  };

  fetchWallets = async () => {
    const seq = ++this.requestSeq;
    const query = this.query;

    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      const response = await this.api.getWallets(query);
      if (seq !== this.requestSeq) return;

      if (!response?.success) {
        throw new Error(response?.message || "Failed to fetch wallets");
      }

      const result = response.result as PaginatedAPIResponse<WalletApiRow>;
      runInAction(() => {
        this.wallets = result?.list || [];
        this.totalCount = result?.total_count || 0;
        this.totalPages = result?.total_pages || 0;
        this.loading = false;
      });
    } catch (error: any) {
      if (seq !== this.requestSeq) return;
      runInAction(() => {
        this.wallets = [];
        this.totalCount = 0;
        this.totalPages = 0;
        this.loading = false;
        this.error =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch wallets";
      });
    }
  };

  openTransactionsDrawer = (
    walletId: number,
    customerName?: string,
    walletType: WalletType = WalletType.CUSTOMER,
  ) => {
    this.transactionsDrawerWalletId = walletId;
    this.transactionsDrawerCustomerName = customerName || null;
    this.transactionsDrawerWalletType = walletType;
    this.transactionsQuery = buildDefaultWalletTransactionQuery(walletId);
    this.transactions = [];
    this.transactionsError = null;
  };

  closeTransactionsDrawer = () => {
    this.transactionsDrawerWalletId = null;
    this.transactionsDrawerCustomerName = null;
    this.transactionsDrawerWalletType = WalletType.CUSTOMER;
    this.transactionsQuery = null;
    this.transactions = [];
    this.transactionsTotalCount = 0;
    this.transactionsTotalPages = 0;
    this.transactionsLoading = false;
    this.transactionsDownloadLoading = false;
    this.transactionsError = null;
  };

  setTransactionsQuery = (patch: Partial<WalletTransactionListQuery>) => {
    if (!this.transactionsQuery) return;
    this.transactionsQuery = { ...this.transactionsQuery, ...patch };
  };

  clearTransactionsError = () => {
    this.transactionsError = null;
  };

  fetchTransactions = async () => {
    if (!this.transactionsQuery) return;
    const seq = ++this.transactionsRequestSeq;
    const query = this.transactionsQuery;

    runInAction(() => {
      this.transactionsLoading = true;
      this.transactionsError = null;
    });

    try {
      const response = await this.api.getWalletTransactions(query);
      if (seq !== this.transactionsRequestSeq) return;

      if (!response?.success) {
        throw new Error(response?.message || "Failed to fetch transactions");
      }

      const result =
        response.result as PaginatedAPIResponse<WalletTransactionApiRow>;
      runInAction(() => {
        this.transactions = result?.list || [];
        this.transactionsTotalCount = result?.total_count || 0;
        this.transactionsTotalPages = result?.total_pages || 0;
        this.transactionsLoading = false;
      });
    } catch (error: any) {
      if (seq !== this.transactionsRequestSeq) return;
      runInAction(() => {
        this.transactions = [];
        this.transactionsTotalCount = 0;
        this.transactionsTotalPages = 0;
        this.transactionsLoading = false;
        this.transactionsError =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch transactions";
      });
    }
  };

  downloadTransactionsReport =
    async (): Promise<WalletTransactionDownloadResult> => {
      if (!this.transactionsQuery) {
        throw new Error("No transaction query");
      }

      if (
        !this.transactionsQuery.start_date ||
        !this.transactionsQuery.end_date
      ) {
        throw new Error("Select a start and end date to download the report");
      }

      runInAction(() => {
        this.transactionsDownloadLoading = true;
      });

      try {
        return await this.api.downloadWalletTransactions(this.transactionsQuery);
      } finally {
        runInAction(() => {
          this.transactionsDownloadLoading = false;
        });
      }
    };
}
